import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  BriefcaseBusiness,
  Eye,
  Loader2,
  Mail,
  ShieldAlert,
  DatabaseZap,
  Workflow,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ComponentType } from "react";
import { useRef } from "react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getAdminMonitorCampaignRealtimeQueriesServer,
  getAdminMonitorCampaignsServer,
  type AdminMonitorCampaignItem,
  type AdminMonitorRealtimeQueryItem,
} from "@/server/admin";

export const Route = createFileRoute("/admin/monitor")({
  loader: async () => {
    const monitor = await getAdminMonitorCampaignsServer();
    return { monitor };
  },
  component: AdminMonitorPage,
});

function AdminMonitorPage() {
  const { t } = useTranslation("admin");
  const { monitor } = Route.useLoaderData();
  const [querySheetOpen, setQuerySheetOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<AdminMonitorCampaignItem | null>(null);
  const [realtimeQueries, setRealtimeQueries] = useState<AdminMonitorRealtimeQueryItem[]>([]);
  const [queryLoadingCampaignId, setQueryLoadingCampaignId] = useState<string | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const queryRequestIdRef = useRef(0);

  async function handleViewRealtimeQueries(item: AdminMonitorCampaignItem): Promise<void> {
    const requestId = queryRequestIdRef.current + 1;
    queryRequestIdRef.current = requestId;
    setSelectedCampaign(item);
    setRealtimeQueries([]);
    setQueryError(null);
    setQuerySheetOpen(true);
    setQueryLoadingCampaignId(item.campaign_id);

    try {
      const result = await getAdminMonitorCampaignRealtimeQueriesServer({
        data: { campaignId: item.campaign_id },
      });
      if (queryRequestIdRef.current !== requestId) {
        return;
      }
      setRealtimeQueries(result.items);
    } catch (error) {
      if (queryRequestIdRef.current !== requestId) {
        return;
      }
      setQueryError(extractErrorMessage(error, t("monitor.realtimeQueries.loadError")));
    } finally {
      if (queryRequestIdRef.current === requestId) {
        setQueryLoadingCampaignId(null);
      }
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b px-6 py-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">{t("monitor.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("monitor.description")}</p>
          <p className="text-xs text-muted-foreground">{t("monitor.caliber")}</p>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <SummaryCard
            icon={DatabaseZap}
            label={t("monitor.summary.eligibleWorkspaces")}
            value={monitor.summary.eligible_workspace_count}
          />
          <SummaryCard
            icon={Activity}
            label={t("monitor.summary.activeCampaigns")}
            value={monitor.summary.active_campaign_count}
          />
          <SummaryCard
            icon={Workflow}
            label={t("monitor.summary.normalCampaigns")}
            value={monitor.summary.normal_campaign_count}
          />
          <SummaryCard
            icon={ShieldAlert}
            label={t("monitor.summary.abnormalCampaigns")}
            value={monitor.summary.abnormal_campaign_count}
            tone="danger"
          />
          <SummaryCard
            icon={BriefcaseBusiness}
            label={t("monitor.summary.totalNewTargetCompanies")}
            value={monitor.summary.total_new_target_company_count}
          />
          <SummaryCard
            icon={BriefcaseBusiness}
            label={t("monitor.summary.totalNewOpportunities")}
            value={monitor.summary.total_new_opportunities_count}
          />
          <SummaryCard
            icon={Mail}
            label={t("monitor.summary.totalNewOutbound")}
            value={monitor.summary.total_new_outbound_count}
          />
        </section>

        <section className="mt-6 rounded-2xl border">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <div>
              <h3 className="text-sm font-semibold">{t("monitor.table.title")}</h3>
              <p className="text-xs text-muted-foreground">
                {t("monitor.table.subtitle", {
                  count: monitor.campaigns.length,
                  generatedAt: formatDateTime(monitor.generated_at),
                })}
              </p>
            </div>
            <Badge variant={monitor.temporal_available ? "secondary" : "outline"}>
              {monitor.temporal_available
                ? t("monitor.temporal.available")
                : t("monitor.temporal.unavailable")}
            </Badge>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("monitor.table.columns.campaignId")}</TableHead>
                  <TableHead>{t("monitor.table.columns.campaignName")}</TableHead>
                  <TableHead>{t("monitor.table.columns.workspaceSlug")}</TableHead>
                  <TableHead>{t("monitor.table.columns.status")}</TableHead>
                  <TableHead className="text-right">
                    {t("monitor.table.columns.newTargetCompanies")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("monitor.table.columns.newPending")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("monitor.table.columns.newContacted")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("monitor.table.columns.newRejected")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("monitor.table.columns.newOpportunities")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("monitor.table.columns.newOutbound")}
                  </TableHead>
                  <TableHead className="text-right">{t("monitor.table.columns.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monitor.campaigns.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      {t("monitor.table.empty")}
                    </TableCell>
                  </TableRow>
                ) : (
                  monitor.campaigns.map((item) => (
                    <TableRow key={item.campaign_id}>
                      <TableCell className="font-mono text-xs">
                        <Link
                          to="/workspace/$workspaceSlug/campaigns/$campaignId"
                          search={{
                            tab: "conversation",
                          }}
                          params={{
                            workspaceSlug: item.workspace_slug,
                            campaignId: item.campaign_id,
                          }}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          {item.campaign_id}
                        </Link>
                      </TableCell>
                      <TableCell>{item.campaign_name || "-"}</TableCell>
                      <TableCell>
                        <Link
                          to="/admin/workspaces"
                          search={{
                            workspaceId: item.workspace_id,
                            page: 1,
                            sortBy: "campaign_count",
                          }}
                          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                        >
                          {item.workspace_slug || "-"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={item.status} t={t} />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.new_target_company_count}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.new_pending_target_company_count}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.new_contacted_target_company_count}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.new_rejected_target_company_count}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.new_opportunities_count}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {item.new_outbound_count}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            void handleViewRealtimeQueries(item);
                          }}
                          disabled={queryLoadingCampaignId === item.campaign_id}
                        >
                          {queryLoadingCampaignId === item.campaign_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                          {t("monitor.table.actions.view")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>

      <RealtimeQueriesSheet
        open={querySheetOpen}
        onOpenChange={setQuerySheetOpen}
        campaign={selectedCampaign}
        items={realtimeQueries}
        isLoading={queryLoadingCampaignId !== null}
        error={queryError}
        t={t}
      />
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  tone = "default",
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: number;
  tone?: "default" | "danger";
}) {
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
        </div>
        <div
          className={[
            "rounded-xl p-2",
            tone === "danger" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
          ].join(" ")}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, t }: { status: string; t: (key: string) => string }) {
  if (status === "normal") {
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600">{t("monitor.status.normal")}</Badge>
    );
  }

  return <Badge variant="destructive">{t("monitor.status.abnormal")}</Badge>;
}

function RealtimeQueriesSheet({
  open,
  onOpenChange,
  campaign,
  items,
  isLoading,
  error,
  t,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: AdminMonitorCampaignItem | null;
  items: AdminMonitorRealtimeQueryItem[];
  isLoading: boolean;
  error: string | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[92vw] gap-0 p-0 sm:max-w-5xl">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle>{t("monitor.realtimeQueries.title")}</SheetTitle>
          <SheetDescription>
            {campaign
              ? t("monitor.realtimeQueries.description", {
                  campaignName: campaign.campaign_name || campaign.campaign_id,
                })
              : t("monitor.realtimeQueries.emptySelection")}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("monitor.realtimeQueries.loading")}
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-sm text-muted-foreground">
              {t("monitor.realtimeQueries.empty")}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-56">
                      {t("monitor.realtimeQueries.columns.keyword")}
                    </TableHead>
                    <TableHead>{t("monitor.realtimeQueries.columns.countryCode")}</TableHead>
                    <TableHead>{t("monitor.realtimeQueries.columns.plannedForAt")}</TableHead>
                    <TableHead>{t("monitor.realtimeQueries.columns.status")}</TableHead>
                    <TableHead className="min-w-56">
                      {t("monitor.realtimeQueries.columns.jobId")}
                    </TableHead>
                    <TableHead>{t("monitor.realtimeQueries.columns.completedAt")}</TableHead>
                    <TableHead>{t("monitor.realtimeQueries.columns.createdAt")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={`${item.keyword}-${item.country_code}-${item.created_at}`}>
                      <TableCell className="max-w-80 whitespace-normal break-words font-medium">
                        {item.keyword}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{item.country_code}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDateTime(item.planned_for_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.status}</Badge>
                      </TableCell>
                      <TableCell className="max-w-72 whitespace-normal break-all font-mono text-xs">
                        {item.job_id ? (
                          <a
                            href={`https://realtime-dataservice.47.252.19.107.nip.io/search/${encodeURIComponent(item.job_id)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline-offset-4 hover:underline"
                          >
                            {item.job_id}
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {item.completed_at ? formatDateTime(item.completed_at) : "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDateTime(item.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function formatDateTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message) as { detail?: unknown };
      if (typeof parsed.detail === "string") {
        return parsed.detail;
      }
    } catch {
      return error.message || fallback;
    }
    return error.message || fallback;
  }
  return fallback;
}
